/**
 * StopsCategorizeDialog — « POI à catégoriser » : suggestions par mot-clé du nom, à confirmer ligne par ligne.
 * Aucune application automatique : seules les lignes explicitement sélectionnées puis confirmées sont modifiées.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ActionButton, StatusBadge } from "@/components/ds";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tags, Check } from "lucide-react";
import { CATEGORIES } from "@/lib/stopStyles";

const FIELD = "border border-zinc-900 px-2 py-1 text-xs bg-white";
const catLabel = (k) => CATEGORIES.find((c) => c.key === k)?.label || k || "—";

export default function StopsCategorizeDialog({ open, onClose, onApplied }) {
  const [items, setItems] = useState([]);
  const [finals, setFinals] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [ignored, setIgnored] = useState(new Set());
  const [filter, setFilter] = useState("SUGGESTED");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/stops/suggestions");
      setItems(data.items); setFinals(Object.fromEntries(data.items.map((i) => [i.stop_id, i.suggestion || ""])));
      setSelected(new Set());
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) { setIgnored(new Set()); setFilter("SUGGESTED"); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => items.filter((i) => !ignored.has(i.stop_id) && (filter === "ALL" || (filter === "SUGGESTED" ? !!i.suggestion : !i.suggestion))), [items, ignored, filter]);
  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const apply = async (ids) => {
    const payload = ids.filter((id) => finals[id]).map((id) => {
      const it = items.find((x) => x.stop_id === id);
      return { stop_id: id, category: finals[id], category_source: finals[id] === it.suggestion ? "USER_CONFIRMED_SUGGESTION" : "MANUAL" };
    });
    if (!payload.length) { toast.error("Choisissez une catégorie pour les lignes sélectionnées."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/stops/categorize", { items: payload });
      toast.success(`${data.updated} POI catégorisé(s)`);
      if (data.errors?.length) toast.error(`${data.errors.length} erreur(s)`);
      onApplied && onApplied(data);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl border border-zinc-900 rounded-none p-0" data-testid="categorize-dialog">
        <DialogHeader className="bg-zinc-900 text-white px-4 py-3">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2"><Tags size={16} className="text-[#FFC60A]" /> POI à catégoriser</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Suggestions calculées à partir du nom (mots-clés). Rien n'est appliqué sans votre confirmation explicite, ligne par ligne ou sur les lignes cochées. Ni stop_id ni coordonnées ne sont modifiés.
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {[["SUGGESTED", `Avec suggestion ${items.filter((i) => i.suggestion).length}`], ["NONE", `Sans suggestion ${items.filter((i) => !i.suggestion).length}`], ["ALL", `Tous ${items.length}`]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`categorize-filter-${k}`} onClick={() => setFilter(k)}
                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-zinc-900 ${filter === k ? "bg-zinc-900 text-[#FFC60A]" : "bg-white"}`}>{l}</button>
            ))}
            <span className="ml-auto font-mono text-zinc-500" data-testid="categorize-selected-count">{selected.size} sélectionné(s)</span>
          </div>
          <div className="max-h-[420px] overflow-auto border border-zinc-300">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900 text-white sticky top-0"><tr>
                <th className="px-2 py-1"><input type="checkbox" data-testid="categorize-select-visible" checked={rows.length > 0 && rows.every((r) => selected.has(r.stop_id))}
                  onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.stop_id)) : new Set())} title="Sélectionner les lignes visibles" /></th>
                {["Nom", "Catégorie actuelle", "Suggestion", "Confiance / motif", "Catégorie finale", ""].map((h) => <th key={h} className="px-2 py-1 text-left font-mono text-[10px]">{h}</th>)}
              </tr></thead>
              <tbody data-testid="categorize-body">
                {rows.map((r) => (
                  <tr key={r.stop_id} className="border-t border-zinc-200" data-testid={`categorize-row-${r.stop_id}`}>
                    <td className="px-2 py-1"><input type="checkbox" checked={selected.has(r.stop_id)} onChange={() => toggle(r.stop_id)} data-testid={`categorize-check-${r.stop_id}`} /></td>
                    <td className="px-2 py-1 font-medium">{r.name}{r.active === false && <span className="text-zinc-400"> · inactif</span>}</td>
                    <td className="px-2 py-1"><StatusBadge tone="neutral" label="Autre" size="sm" withDot={false} /></td>
                    <td className="px-2 py-1" data-testid={`categorize-suggestion-${r.stop_id}`}>{r.suggestion ? <StatusBadge tone="info" label={catLabel(r.suggestion)} size="sm" withDot={false} /> : <span className="text-zinc-400">—</span>}</td>
                    <td className="px-2 py-1 text-zinc-600">{r.confidence ? `${r.confidence === "high" ? "Élevée" : r.confidence === "medium" ? "Moyenne" : "Ambiguë"} · ` : ""}{r.reason}</td>
                    <td className="px-2 py-1">
                      <select className={FIELD} value={finals[r.stop_id] || ""} onChange={(e) => setFinals((f) => ({ ...f, [r.stop_id]: e.target.value }))} data-testid={`categorize-final-${r.stop_id}`}>
                        <option value="">— choisir —</option>
                        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <button type="button" className="px-2 py-0.5 border border-zinc-900 bg-[#FFC60A] text-[10px] font-bold uppercase disabled:opacity-40" disabled={!finals[r.stop_id] || busy}
                        onClick={() => apply([r.stop_id])} data-testid={`categorize-confirm-${r.stop_id}`}>Confirmer</button>
                      <button type="button" className="ml-1 px-2 py-0.5 border border-zinc-300 text-[10px] uppercase" onClick={() => setIgnored((p) => new Set(p).add(r.stop_id))} data-testid={`categorize-ignore-${r.stop_id}`}>Ignorer</button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-zinc-500">Aucun POI dans ce filtre.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center gap-2 pt-2 border-t border-zinc-200">
            <span className="text-[11px] text-zinc-500">« Ignorer » ne modifie rien (masqué pour cette session).</span>
            <div className="flex gap-2">
              <ActionButton variant="ghost" onClick={onClose} testId="categorize-close">Fermer</ActionButton>
              <ActionButton variant="primary" icon={Check} loading={busy} disabled={selected.size === 0} onClick={() => apply([...selected])} testId="categorize-confirm-selected">
                Confirmer la sélection ({selected.size})
              </ActionButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
